import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Form, Spinner, Alert, Badge } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { PlusCircle, Filter, Calendar, MapPin, Clock } from 'react-feather';
import { getEvents, createEvent, updateEvent, deleteEvent } from '../services/api';
import EventCard from '../components/EventCard';

interface Event {
  id: string;
  name: string;
  description?: string;
  location?: string;
  start_date?: string;
  end_date?: string;
  active: boolean;
  created_at: string;
}

const EventManagement: React.FC = () => {
  const { t } = useTranslation();
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showActiveOnly, setShowActiveOnly] = useState(false);
  
  // Create/Edit event states
  const [showEventModal, setShowEventModal] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [currentEvent, setCurrentEvent] = useState<Event | null>(null);
  const [eventForm, setEventForm] = useState({
    name: '',
    description: '',
    location: '',
    start_date: '',
    end_date: '',
    active: true
  });
  const [formErrors, setFormErrors] = useState<{[key: string]: string}>({});
  const [submitting, setSubmitting] = useState(false);
  
  // Delete confirmation modal
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [eventToDelete, setEventToDelete] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);

  useEffect(() => {
    fetchEvents();
  }, [showActiveOnly]);

  const fetchEvents = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await getEvents(showActiveOnly);
      setEvents(response.data);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to load events');
      console.error('Error fetching events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateEvent = () => {
    setEventForm({
      name: '',
      description: '',
      location: '',
      start_date: '',
      end_date: '',
      active: true
    });
    setFormErrors({});
    setIsEditMode(false);
    setCurrentEvent(null);
    setShowEventModal(true);
  };

  const handleEditEvent = (eventId: string) => {
    const event = events.find(e => e.id === eventId);
    if (event) {
      setCurrentEvent(event);
      setEventForm({
        name: event.name,
        description: event.description || '',
        location: event.location || '',
        start_date: event.start_date ? new Date(event.start_date).toISOString().slice(0, 16) : '',
        end_date: event.end_date ? new Date(event.end_date).toISOString().slice(0, 16) : '',
        active: event.active
      });
      setFormErrors({});
      setIsEditMode(true);
      setShowEventModal(true);
    }
  };

  const handleDeleteClick = (eventId: string) => {
    setEventToDelete(eventId);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!eventToDelete) return;

    try {
      setDeleting(true);
      await deleteEvent(eventToDelete);
      setEvents(events.filter(event => event.id !== eventToDelete));
      setShowDeleteModal(false);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to delete event');
      console.error('Error deleting event:', error);
    } finally {
      setDeleting(false);
      setEventToDelete(null);
    }
  };

  const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setEventForm(prev => ({ ...prev, [name]: checked }));
    } else {
      setEventForm(prev => ({ ...prev, [name]: value }));
    }
    
    // Clear error for this field if exists
    if (formErrors[name]) {
      setFormErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = (): boolean => {
    const errors: {[key: string]: string} = {};
    
    if (!eventForm.name.trim()) {
      errors.name = t('event.createEvent.errors.nameRequired');
    }
    
    // Validate dates if both provided
    if (eventForm.start_date && eventForm.end_date) {
      const startDate = new Date(eventForm.start_date);
      const endDate = new Date(eventForm.end_date);
      
      if (endDate < startDate) {
        errors.end_date = t('event.createEvent.errors.endDateBeforeStart');
      }
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setSubmitting(true);
      
      const eventData = {
        name: eventForm.name,
        description: eventForm.description,
        location: eventForm.location,
        start_date: eventForm.start_date,
        end_date: eventForm.end_date,
        active: eventForm.active
      };
      
      if (isEditMode && currentEvent) {
        // Update existing event
        const response = await updateEvent(currentEvent.id, eventData);
        const updatedEvent = response.data;
        
        setEvents(events.map(event => 
          event.id === updatedEvent.id ? updatedEvent : event
        ));
      } else {
        // Create new event
        const response = await createEvent(eventData);
        const newEvent = response.data;
        
        setEvents([newEvent, ...events]);
      }
      
      setShowEventModal(false);
    } catch (error: any) {
      setError(error.response?.data?.error || 'Failed to save event');
      console.error('Error saving event:', error);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Container className="py-4">
      <Row className="mb-4">
        <Col>
          <div className="d-flex flex-wrap justify-content-between align-items-center">
            <div>
              <h1 className="mb-1">{t('event.management.title')}</h1>
              <p className="text-muted">{t('event.management.subtitle')}</p>
            </div>
            <Button
              variant="primary"
              className="d-flex align-items-center"
              onClick={handleCreateEvent}
            >
              <PlusCircle size={16} className="me-2" />
              {t('event.management.createButton')}
            </Button>
          </div>
        </Col>
      </Row>

      {error && (
        <Alert variant="danger" onClose={() => setError('')} dismissible>
          {error}
        </Alert>
      )}

      <Row className="mb-4">
        <Col>
          <Card className="shadow-sm">
            <Card.Body className="d-flex justify-content-between align-items-center">
              <div className="d-flex align-items-center">
                <Filter size={18} className="me-2 text-muted" />
                <span>{t('event.management.filters')}</span>
              </div>
              <Form.Check 
                type="switch"
                id="active-filter"
                label={t('event.management.filterActive')}
                checked={showActiveOnly}
                onChange={() => setShowActiveOnly(!showActiveOnly)}
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <p className="mt-3">{t('loading')}</p>
        </div>
      ) : events.length > 0 ? (
        <Row className="g-4">
          {events.map(event => (
            <Col key={event.id} md={6} lg={4}>
              <EventCard
                id={event.id}
                name={event.name}
                description={event.description}
                location={event.location}
                startDate={event.start_date}
                endDate={event.end_date}
                active={event.active}
                isOwner={true}
                onEdit={handleEditEvent}
                onDelete={handleDeleteClick}
              />
            </Col>
          ))}
        </Row>
      ) : (
        <Card className="shadow-sm text-center py-5">
          <Card.Body>
            <h5 className="text-muted mb-3">{t('event.management.noEvents')}</h5>
            <Button variant="primary" onClick={handleCreateEvent}>
              {t('event.management.createButton')}
            </Button>
          </Card.Body>
        </Card>
      )}

      {/* Create/Edit Event Modal */}
      <Modal 
        show={showEventModal} 
        onHide={() => setShowEventModal(false)}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {isEditMode ? t('event.createEvent.editTitle') : t('event.createEvent.title')}
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleSubmit}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>{t('event.createEvent.eventName')}</Form.Label>
              <Form.Control
                type="text"
                name="name"
                value={eventForm.name}
                onChange={handleFormChange}
                placeholder={t('event.createEvent.eventNamePlaceholder') as string}
                isInvalid={!!formErrors.name}
              />
              <Form.Control.Feedback type="invalid">
                {formErrors.name}
              </Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('event.createEvent.description')}</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={eventForm.description}
                onChange={handleFormChange}
                placeholder={t('event.createEvent.descriptionPlaceholder') as string}
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>{t('event.createEvent.location')}</Form.Label>
              <Form.Control
                type="text"
                name="location"
                value={eventForm.location}
                onChange={handleFormChange}
                placeholder={t('event.createEvent.locationPlaceholder') as string}
              />
            </Form.Group>

            <Row>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('event.createEvent.startDate')}</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="start_date"
                    value={eventForm.start_date}
                    onChange={handleFormChange}
                  />
                </Form.Group>
              </Col>
              <Col md={6}>
                <Form.Group className="mb-3">
                  <Form.Label>{t('event.createEvent.endDate')}</Form.Label>
                  <Form.Control
                    type="datetime-local"
                    name="end_date"
                    value={eventForm.end_date}
                    onChange={handleFormChange}
                    isInvalid={!!formErrors.end_date}
                  />
                  <Form.Control.Feedback type="invalid">
                    {formErrors.end_date}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>
            </Row>

            <Form.Group className="mb-3">
              <Form.Check 
                type="switch"
                id="event-active"
                label={t('event.createEvent.activeEvent')}
                name="active"
                checked={eventForm.active}
                onChange={handleFormChange}
              />
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button 
              variant="secondary" 
              onClick={() => setShowEventModal(false)}
              disabled={submitting}
            >
              {t('event.createEvent.cancelButton')}
            </Button>
            <Button 
              variant="primary" 
              type="submit"
              disabled={submitting}
            >
              {submitting ? (
                <>
                  <Spinner
                    as="span"
                    animation="border"
                    size="sm"
                    role="status"
                    aria-hidden="true"
                    className="me-2"
                  />
                  {t('loading')}
                </>
              ) : isEditMode ? t('event.createEvent.saveButton') : t('event.createEvent.createButton')}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal
        show={showDeleteModal}
        onHide={() => setShowDeleteModal(false)}
        backdrop="static"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>{t('event.deleteConfirm.title')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>{t('event.management.deleteConfirm')}</p>
          <p className="text-danger fw-bold">{t('event.deleteConfirm.warning')}</p>
        </Modal.Body>
        <Modal.Footer>
          <Button 
            variant="secondary" 
            onClick={() => setShowDeleteModal(false)}
            disabled={deleting}
          >
            {t('event.management.deleteCancel')}
          </Button>
          <Button 
            variant="danger" 
            onClick={handleDeleteConfirm}
            disabled={deleting}
          >
            {deleting ? (
              <>
                <Spinner
                  as="span"
                  animation="border"
                  size="sm"
                  role="status"
                  aria-hidden="true"
                  className="me-2"
                />
                {t('loading')}
              </>
            ) : t('event.management.deleteConfirmButton')}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default EventManagement;
